import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { LogIn } from "lucide-react";

export default function AuthLogin() {
  const { user, loginEmail, registerEmail, startGoogle } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "owner" ? "/admin" : "/profile"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      if (mode === "login") {
        const d = await loginEmail(email, password);
        nav(d.role === "owner" ? "/admin" : "/profile", { replace: true });
      } else {
        const d = await registerEmail(email, password, name || email.split("@")[0]);
        nav(d.role === "owner" ? "/admin" : "/profile", { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="auth-login-page">
      <div className="w-full max-w-md rounded-2xl border border-[#FF3D8B]/30 bg-black/60 backdrop-blur-xl p-8 shadow-[0_0_60px_rgba(255,61,139,0.25)]">
        <div className="flex items-center gap-3 mb-2 text-[#FF3D8B]">
          <LogIn size={18} />
          <span className="uppercase tracking-widest text-xs">
            {mode === "login" ? "Welcome back" : "Join Booty Breeze"}
          </span>
        </div>
        <h1 className="font-display text-4xl text-white mb-6">
          {mode === "login" ? "Sign in" : "Create account"}
        </h1>

        <button
          type="button"
          onClick={startGoogle}
          data-testid="google-signin-btn"
          className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-white/15 bg-white text-black hover:bg-white/90 transition-colors text-sm font-semibold mb-6"
        >
          <svg viewBox="0 0 48 48" width="18" height="18">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.5 2 30 0 24 0 14.6 0 6.4 5.4 2.5 13.4l7.8 6.1C12.3 13.4 17.7 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.5 24.5c0-1.7-.2-3.4-.5-5H24v9.5h12.7c-.5 3-2.3 5.5-4.7 7.1l7.5 5.8c4.4-4 7-10 7-17.4z"/>
            <path fill="#FBBC05" d="M10.3 28.5c-.5-1.4-.8-3-.8-4.5s.3-3.1.8-4.5l-7.8-6.1C.9 16.6 0 20.2 0 24s.9 7.4 2.5 10.6l7.8-6.1z"/>
            <path fill="#34A853" d="M24 48c6 0 11-2 14.7-5.4l-7.5-5.8c-2 1.3-4.6 2.1-7.2 2.1-6.3 0-11.7-3.9-13.7-9.5l-7.8 6.1C6.4 42.6 14.6 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/40 text-xs uppercase tracking-widest">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit}>
          {mode === "register" && (
            <>
              <label className="block text-white/60 text-xs uppercase tracking-wider mb-2">Name</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="How should we call you?"
                data-testid="register-name-input"
                className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#FF3D8B] mb-4"
              />
            </>
          )}
          <label className="block text-white/60 text-xs uppercase tracking-wider mb-2">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="auth-email-input"
            className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#FF3D8B]"
          />
          <label className="block text-white/60 text-xs uppercase tracking-wider mt-4 mb-2">Password</label>
          <input
            type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="auth-password-input"
            className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:border-[#FF3D8B]"
          />
          {error && <div className="mt-4 text-sm text-red-400" data-testid="auth-error">{error}</div>}
          <button
            type="submit" disabled={busy} data-testid="auth-submit-btn"
            className="mt-6 w-full py-3.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] disabled:opacity-40 transition-colors"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="mt-6 text-center text-white/50 text-sm">
          {mode === "login" ? (
            <>New here?{" "}
              <button type="button" onClick={() => setMode("register")} className="text-[#FF3D8B] hover:underline" data-testid="switch-to-register">
                Create an account
              </button>
            </>
          ) : (
            <>Already have one?{" "}
              <button type="button" onClick={() => setMode("login")} className="text-[#FF3D8B] hover:underline" data-testid="switch-to-login">
                Sign in
              </button>
            </>
          )}
        </div>
        <div className="mt-3 text-center">
          <Link to="/" className="text-white/40 hover:text-white text-xs uppercase tracking-widest">
            Back to shop
          </Link>
        </div>
      </div>
    </div>
  );
}
